import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LandingLayout from './components/LandingLayout';
import Section from './components/Section';
import ALL_BLOG_POSTS from './data/blog-posts';
import BLOG_THUMBNAILS from './data/blog-thumbnails';

const SHADOW = '4px 4px 0px #1e1e1e';

export default function BlogPage() {
  const navigate = useNavigate();

  return (
    <LandingLayout>
      <Section background="#F7F7F7" py={{ xs: 8, md: 12 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              color: '#2C2C2C',
              mb: 2,
            }}
          >
            Blog
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lato, Inter, sans-serif',
              fontSize: { xs: '1rem', md: '1.15rem' },
              color: '#555',
              maxWidth: 550,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Insights on writing instruction, media literacy, and educational technology.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 3,
          }}
        >
          {ALL_BLOG_POSTS.filter((p) => !p.draft).map((post) => {
            const thumbnail = BLOG_THUMBNAILS[post.slug];
            return (
              <Box
                key={post.slug}
                onClick={() => navigate(`/blog/${post.slug}`)}
                sx={{
                  borderRadius: '6px',
                  border: '2px solid #1e1e1e',
                  boxShadow: SHADOW,
                  backgroundColor: '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'all 100ms ease-in-out',
                  '&:hover': {
                    boxShadow: '2px 2px 0px #1e1e1e',
                    transform: 'translate(2px, 2px)',
                  },
                }}
              >
                {/* Thumbnail */}
                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                    backgroundColor: thumbnail ? 'transparent' : '#E7E7E7',
                    overflow: 'hidden',
                    borderBottom: '2px solid #1e1e1e',
                  }}
                >
                  {thumbnail ? (
                    <Box
                      component="img"
                      src={thumbnail}
                      alt={post.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '2.5rem', opacity: 0.3 }}>
                        ✍️
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '0.75rem',
                      color: '#888',
                      mb: 1,
                    }}
                  >
                    {post.author}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: '#2C2C2C',
                      lineHeight: 1.4,
                      mb: 1.5,
                    }}
                  >
                    {post.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '0.85rem',
                      color: '#666',
                      lineHeight: 1.6,
                      flex: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.excerpt}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      color: '#2C2C2C',
                      mt: 2,
                    }}
                  >
                    Read more &rarr;
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Section>
    </LandingLayout>
  );
}
